import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  HelpCircle,
  ShoppingBag,
  Shield,
  Store,
  CreditCard,
  MessageSquare,
  User,
  Package,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Mail,
  Phone,
  Globe,
  Smartphone,
  Truck,
  RefreshCw,
  Star,
  Lock,
  Bell,
  Filter,
  X,
  FileText,
  CheckCircle,
  Leaf
} from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { useAuth } from '../context/AuthContext';
import MobileBottomNav from '../components/layout/MobileBottomNav';

const FAQPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [expandedQuestions, setExpandedQuestions] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const searchInputRef = useRef(null);

  const brandColor = '#ff660e';
  const brandColorLight = '#fff0e6';

  const faqCategories = [
    { id: 'all', name: 'All Questions', icon: <Globe className="w-5 h-5" />, count: 0 },
    { id: 'buying', name: 'Buying', icon: <ShoppingBag className="w-5 h-5" />, count: 8 },
    { id: 'selling', name: 'Selling', icon: <Store className="w-5 h-5" />, count: 7 },
    { id: 'escrow', name: 'Escrow', icon: <Shield className="w-5 h-5" />, count: 6 },
    { id: 'account', name: 'Account', icon: <User className="w-5 h-5" />, count: 5 },
    { id: 'payments', name: 'Payments', icon: <CreditCard className="w-5 h-5" />, count: 4 },
    { id: 'delivery', name: 'Delivery', icon: <Truck className="w-5 h-5" />, count: 4 },
    { id: 'safety', name: 'Safety', icon: <Lock className="w-5 h-5" />, count: 5 },
    { id: 'disputes', name: 'Disputes', icon: <AlertCircle className="w-5 h-5" />, count: 4 },
    { id: 'technical', name: 'Technical', icon: <Smartphone className="w-5 h-5" />, count: 3 }
  ];

  const allFAQs = [
    // Buying FAQs
    {
      id: 1,
      category: 'buying',
      question: 'How do I buy a product on EcoMart?',
      answer: 'Browse products, contact the seller through our secure chat, agree on terms, use escrow for payment protection, arrange meetup/delivery, inspect the product, then release funds once satisfied.',
      tags: ['buying', 'process', 'basics']
    },
    {
      id: 2,
      category: 'buying',
      question: 'How do I contact a seller?',
      answer: 'Click the "Message Seller" button on any product listing. All communications should stay within EcoMart for security and dispute resolution purposes.',
      tags: ['communication', 'sellers', 'chat']
    },
    {
      id: 3,
      category: 'buying',
      question: 'Can I negotiate the price?',
      answer: 'Yes, you can negotiate prices with sellers through our chat system. Always be respectful and reasonable in your offers.',
      tags: ['negotiation', 'pricing', 'offers']
    },
    {
      id: 4,
      category: 'buying',
      question: 'How long do I have to inspect a product?',
      answer: 'You have 48 hours to inspect the product after delivery/meetup. During this time, funds are held in escrow and you can raise concerns if needed.',
      tags: ['inspection', 'escrow', 'timeframe']
    },
    {
      id: 5,
      category: 'buying',
      question: 'What if I receive a damaged or wrong item?',
      answer: 'Do NOT release escrow funds. Contact the seller immediately and raise a dispute through our system. Provide photos/videos as evidence.',
      tags: ['damaged', 'dispute', 'quality']
    },
    {
      id: 6,
      category: 'buying',
      question: 'How do I know if a seller is trustworthy?',
      answer: 'Check their seller rating, review count, verification status, and response rate. Also review their transaction history and customer feedback.',
      tags: ['trust', 'verification', 'ratings']
    },
    {
      id: 7,
      category: 'buying',
      question: 'Can I cancel a purchase?',
      answer: 'You can cancel before the seller accepts your order. Once accepted, you need seller agreement to cancel. After delivery starts, cancellation is not possible.',
      tags: ['cancellation', 'orders', 'refunds']
    },
    {
      id: 8,
      category: 'buying',
      question: 'How do I save items for later?',
      answer: 'Click the heart icon on any listing to add it to your favorites. You can access all saved items from your profile favorites section.',
      tags: ['favorites', 'saving', 'wishlist']
    },

    // Selling FAQs
    {
      id: 9,
      category: 'selling',
      question: 'How do I start selling on EcoMart?',
      answer: 'Set up your store profile, get moderator approval, take quality photos, create detailed listings, and enable escrow for secure transactions.',
      tags: ['setup', 'store', 'beginner']
    },
    {
      id: 10,
      category: 'selling',
      question: 'How long does store approval take?',
      answer: 'Store approval typically takes 24-48 hours. Our moderators review all new stores to ensure quality and safety standards.',
      tags: ['approval', 'moderation', 'timeframe']
    },
    {
      id: 11,
      category: 'selling',
      question: 'Is there a fee to list items?',
      answer: 'No, listing items on EcoMart is completely free. We only charge processing fees when using our escrow service for transactions.',
      tags: ['fees', 'listing', 'cost']
    },
    {
      id: 12,
      category: 'selling',
      question: 'How do I boost my listings?',
      answer: 'Use promotional tools, share on social media, renew listings regularly, offer bundle deals, and maintain high ratings for better visibility.',
      tags: ['promotion', 'visibility', 'marketing']
    },
    {
      id: 13,
      category: 'selling',
      question: 'When do I get paid?',
      answer: 'You receive payment after the buyer confirms satisfaction and releases escrow funds. This typically happens within 48 hours of delivery.',
      tags: ['payment', 'timing', 'escrow']
    },
    {
      id: 14,
      category: 'selling',
      question: 'What should I do if a buyer doesn\'t release funds?',
      answer: 'Wait for the 48-hour inspection period. If funds aren\'t released after that, contact the buyer through chat. If no response, raise a dispute.',
      tags: ['funds', 'dispute', 'timing']
    },
    {
      id: 15,
      category: 'selling',
      question: 'Can I sell services, not just products?',
      answer: 'Yes, you can list services. Use our milestone-based escrow system where funds are released as you complete agreed-upon stages of work.',
      tags: ['services', 'freelance', 'milestones']
    },

    // Escrow FAQs
    {
      id: 16,
      category: 'escrow',
      question: 'What is EcoMart Escrow?',
      answer: 'Our escrow service holds buyer payments securely until both parties are satisfied with the transaction. It protects both buyers and sellers.',
      tags: ['basics', 'protection', 'security']
    },
    {
      id: 17,
      category: 'escrow',
      question: 'How do I use escrow as a buyer?',
      answer: 'Select "Pay with Escrow" at checkout. Your payment is held securely until you receive and inspect the item, then you release the funds.',
      tags: ['buyer', 'process', 'checkout']
    },
    {
      id: 18,
      category: 'escrow',
      question: 'How do I enable escrow as a seller?',
      answer: 'Go to Store Settings → Payment Options → Enable Escrow. You can choose to enable it for all listings or select specific ones.',
      tags: ['seller', 'setup', 'settings']
    },
    {
      id: 19,
      category: 'escrow',
      question: 'Are there fees for using escrow?',
      answer: 'Processing fees apply to escrow transactions. The fee percentage depends on the transaction amount and is clearly displayed before payment.',
      tags: ['fees', 'cost', 'pricing']
    },
    {
      id: 20,
      category: 'escrow',
      question: 'How long are funds held in escrow?',
      answer: 'Typically 3-7 business days, depending on delivery method and the 48-hour inspection period after delivery.',
      tags: ['duration', 'timing', 'holding']
    },
    {
      id: 21,
      category: 'escrow',
      question: 'Can I cancel an escrow payment?',
      answer: 'Buyers can cancel before seller acceptance. After acceptance, both parties must agree to cancel. After delivery starts, cancellation is not possible.',
      tags: ['cancellation', 'refund', 'termination']
    },

    // Account FAQs
    {
      id: 22,
      category: 'account',
      question: 'How do I verify my account?',
      answer: 'Go to Profile → Verification and follow the steps. Verification increases trust and unlocks additional features like higher transaction limits.',
      tags: ['verification', 'trust', 'security']
    },
    {
      id: 23,
      category: 'account',
      question: 'How do I reset my password?',
      answer: 'Click "Forgot Password" on the login page. You\'ll receive an email with instructions to reset your password securely.',
      tags: ['password', 'security', 'recovery']
    },
    {
      id: 24,
      category: 'account',
      question: 'Can I have multiple stores?',
      answer: 'Each verified account can create one primary store. For multiple business lines, contact support for enterprise solutions.',
      tags: ['stores', 'multiple', 'business']
    },
    {
      id: 25,
      category: 'account',
      question: 'How do I delete my account?',
      answer: 'Contact support to request account deletion. Note: You must resolve all active transactions and disputes before deletion.',
      tags: ['deletion', 'privacy', 'closure']
    },
    {
      id: 26,
      category: 'account',
      question: 'How do I update my contact information?',
      answer: 'Go to Profile → Settings → Contact Information. Some changes may require verification for security purposes.',
      tags: ['profile', 'contact', 'update']
    },

    // Payment FAQs
    {
      id: 27,
      category: 'payments',
      question: 'What payment methods are accepted?',
      answer: 'We accept credit/debit cards, bank transfers, and mobile money. All payments go through our secure payment processor.',
      tags: ['methods', 'cards', 'banking']
    },
    {
      id: 28,
      category: 'payments',
      question: 'Are there transaction limits?',
      answer: 'Yes, limits vary based on your verification level. Unverified accounts have lower limits. Verify your account for higher limits.',
      tags: ['limits', 'verification', 'restrictions']
    },
    {
      id: 29,
      category: 'payments',
      question: 'How long do refunds take?',
      answer: 'Refunds typically process within 5-10 business days, depending on your payment method and bank processing times.',
      tags: ['refunds', 'timing', 'processing']
    },
    {
      id: 30,
      category: 'payments',
      question: 'Is my payment information secure?',
      answer: 'Yes, we use bank-level encryption and never store your full payment details. All transactions are processed through secure PCI-compliant systems.',
      tags: ['security', 'encryption', 'privacy']
    },

    // Delivery FAQs
    {
      id: 31,
      category: 'delivery',
      question: 'How do I arrange delivery?',
      answer: 'Discuss delivery options with the seller in chat. You can arrange meetup, local delivery, or shipping with tracking.',
      tags: ['shipping', 'meetup', 'logistics']
    },
    {
      id: 32,
      category: 'delivery',
      question: 'Who pays for delivery?',
      answer: 'This is negotiated between buyer and seller. Typically, sellers include delivery costs in the price or charge separately.',
      tags: ['cost', 'shipping', 'negotiation']
    },
    {
      id: 33,
      category: 'delivery',
      question: 'What if my item gets lost during delivery?',
      answer: 'Contact the seller first. If using tracked shipping, file a claim with the shipping provider. Raise a dispute if the issue isn\'t resolved.',
      tags: ['lost', 'damage', 'claims']
    },
    {
      id: 34,
      category: 'delivery',
      question: 'Can I arrange international shipping?',
      answer: 'Yes, but both parties must agree on shipping costs, methods, and customs duties. Use tracked shipping with insurance for international items.',
      tags: ['international', 'shipping', 'customs']
    },

    // Safety FAQs
    {
      id: 35,
      category: 'safety',
      question: 'How do I stay safe during meetups?',
      answer: 'Meet in well-lit public places during daylight hours, bring a friend, inform someone of your meeting, and keep communication within the app.',
      tags: ['meetup', 'safety', 'precautions']
    },
    {
      id: 36,
      category: 'safety',
      question: 'What should I do if I encounter a scam?',
      answer: 'Immediately stop communication, do NOT send any money, report the user through their profile, and contact our support team with details.',
      tags: ['scams', 'reporting', 'safety']
    },
    {
      id: 37,
      category: 'safety',
      question: 'How does EcoMart protect my personal information?',
      answer: 'We use end-to-end encryption, never share your contact details without permission, and follow strict data protection regulations.',
      tags: ['privacy', 'data', 'protection']
    },
    {
      id: 38,
      category: 'safety',
      question: 'Can I share my contact information?',
      answer: 'We recommend keeping all communication within EcoMart for your protection. If needed, use our secure contact sharing feature.',
      tags: ['contact', 'communication', 'security']
    },
    {
      id: 39,
      category: 'safety',
      question: 'What if someone asks for direct payment?',
      answer: 'This is against our terms. Always use EcoMart escrow. Report any user asking for direct payment outside our platform.',
      tags: ['direct-payment', 'scams', 'report']
    },

    // Dispute FAQs
    {
      id: 40,
      category: 'disputes',
      question: 'How do I raise a dispute?',
      answer: 'Go to the order details and click "Raise Dispute." Provide evidence and explanation. Our team will mediate within 48 hours.',
      tags: ['process', 'mediation', 'conflict']
    },
    {
      id: 41,
      category: 'disputes',
      question: 'What evidence should I provide?',
      answer: 'Photos/videos of the item, chat screenshots, delivery proof, tracking information, and any other relevant documentation.',
      tags: ['evidence', 'proof', 'documentation']
    },
    {
      id: 42,
      category: 'disputes',
      question: 'How long does dispute resolution take?',
      answer: 'Most disputes are resolved within 48-72 hours after all evidence is submitted. Complex cases may take longer.',
      tags: ['timing', 'resolution', 'mediation']
    },
    {
      id: 43,
      category: 'disputes',
      question: 'Can I appeal a dispute decision?',
      answer: 'Yes, you can request a review within 7 days of the decision by providing new evidence or information not previously considered.',
      tags: ['appeal', 'review', 'decision']
    },

    // Technical FAQs
    {
      id: 44,
      category: 'technical',
      question: 'Is there a mobile app?',
      answer: 'Yes, EcoMart is available on both iOS and Android. Download from the App Store or Google Play Store.',
      tags: ['app', 'mobile', 'download']
    },
    {
      id: 45,
      category: 'technical',
      question: 'The website/app isn\'t loading properly',
      answer: 'Try clearing your browser cache, updating the app, or switching to a different browser. If issues persist, contact technical support.',
      tags: ['troubleshooting', 'technical', 'support']
    },
    {
      id: 46,
      category: 'technical',
      question: 'How do I enable notifications?',
      answer: 'Check your device settings and app permissions. On the web, ensure notifications are allowed in your browser settings.',
      tags: ['notifications', 'alerts', 'settings']
    }
  ];

  // Calculate counts for all category
  useEffect(() => {
    const allCategory = faqCategories.find(cat => cat.id === 'all');
    if (allCategory) {
      allCategory.count = allFAQs.length;
    }
  }, []);

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query.trim() && !recentSearches.includes(query.trim())) {
      setRecentSearches(prev => [query.trim(), ...prev.slice(0, 4)]);
    }
  };

  const toggleQuestion = (id) => {
    setExpandedQuestions(prev =>
      prev.includes(id)
        ? prev.filter(qId => qId !== id)
        : [...prev, id]
    );
  };

  const clearSearch = () => {
    setSearchQuery('');
    searchInputRef.current?.focus();
  };

  const filteredFAQs = allFAQs.filter(faq => {
    if (activeCategory !== 'all' && faq.category !== activeCategory) return false;
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      return (
        faq.question.toLowerCase().includes(query) ||
        faq.answer.toLowerCase().includes(query) ||
        faq.tags.some(tag => tag.toLowerCase().includes(query))
      );
    }
    
    return true;
  });

  const popularQuestions = allFAQs.filter(faq => 
    [1, 3, 9, 16, 22, 35, 27, 31].includes(faq.id)
  );

  const getCategoryIcon = (categoryId) => {
    const category = faqCategories.find(cat => cat.id === categoryId);
    return category?.icon || <HelpCircle className="w-5 h-5" />;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-10 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-6"
               style={{ backgroundColor: brandColorLight }}>
            <div className="flex items-center">
              <HelpCircle className="w-8 h-8 ml-1" style={{ color: brandColor }} />
            </div>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            EcoMart Help Center
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Find answers to common questions about buying, selling, and staying safe on our marketplace
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-10">
          <div className="relative">
            <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
              <Search className="w-5 h-5 text-gray-400" />
            </div>
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search for answers..."
              className="w-full pl-12 pr-12 py-4 bg-white rounded-2xl shadow-sm border border-gray-200 focus:outline-none focus:ring-2 focus:border-transparent"
              style={{ focusRingColor: brandColor }}
            />
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Recent Searches */}
          {recentSearches.length > 0 && !searchQuery && (
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="text-sm text-gray-500 mr-2">Recent:</span>
              {recentSearches.map((search, index) => (
                <button
                  key={index}
                  onClick={() => handleSearch(search)}
                  className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-gray-200 transition-colors"
                >
                  {search}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Categories */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Browse by Category</h2>
            <Filter className="w-5 h-5 text-gray-400" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {faqCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`flex flex-col items-center justify-center p-4 rounded-xl transition-all ${
                  activeCategory === category.id
                    ? 'bg-white shadow-md border'
                    : 'bg-white shadow-sm hover:shadow-md'
                }`}
                style={activeCategory === category.id ? { borderColor: brandColor } : {}}
              >
                <div className={`mb-2 ${
                  activeCategory === category.id 
                    ? 'text-orange-500' 
                    : 'text-gray-400'
                }`}>
                  {category.icon}
                </div>
                <span className={`font-medium text-sm ${
                  activeCategory === category.id
                    ? 'text-gray-900'
                    : 'text-gray-600'
                }`}>
                  {category.name}
                </span>
                <span className="text-xs text-gray-500 mt-1">{category.count} questions</span>
              </button>
            ))}
          </div>
        </div>

        {/* Popular Questions */}
        <div className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Popular Questions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {popularQuestions.map((faq) => (
              <button
                key={faq.id}
                onClick={() => toggleQuestion(faq.id)}
                className="bg-white rounded-xl shadow-sm p-5 text-left hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center mr-3"
                           style={{ backgroundColor: brandColorLight }}>
                        <div style={{ color: brandColor }}>
                          {getCategoryIcon(faq.category)}
                        </div>
                      </div>
                      <h3 className="font-semibold text-gray-900">{faq.question}</h3>
                    </div>
                    {expandedQuestions.includes(faq.id) && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <p className="text-gray-600">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                  <div className="ml-4">
                    {expandedQuestions.includes(faq.id) ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* FAQ List */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">
              {activeCategory === 'all' ? 'All Questions' : 
               faqCategories.find(c => c.id === activeCategory)?.name}
              <span className="text-gray-500 font-normal ml-2">
                ({filteredFAQs.length} found{searchQuery && ` for "${searchQuery}"`})
              </span>
            </h2>
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="text-sm flex items-center"
                style={{ color: brandColor }}
              >
                <X className="w-4 h-4 mr-1" />
                Clear search
              </button>
            )}
          </div>

          {filteredFAQs.length > 0 ? (
            <div className="space-y-4">
              {filteredFAQs.map((faq) => (
                <div
                  key={faq.id}
                  className="bg-white rounded-xl shadow-sm overflow-hidden"
                >
                  <button
                    onClick={() => toggleQuestion(faq.id)}
                    className="w-full p-5 text-left"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <div className="w-10 h-10 rounded-lg flex items-center justify-center mr-4"
                                 style={{ backgroundColor: brandColorLight }}>
                              <div style={{ color: brandColor }}>
                                {getCategoryIcon(faq.category)}
                              </div>
                            </div>
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                              {faq.question}
                            </h3>
                            <div className="flex flex-wrap gap-2 mb-3">
                              {faq.tags.map((tag, index) => (
                                <span
                                  key={index}
                                  className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>

                        {expandedQuestions.includes(faq.id) && (
                          <div className="mt-4 pt-4 border-t border-gray-100">
                            <p className="text-gray-600 mb-4">{faq.answer}</p>
                            <div className="flex items-center text-sm text-gray-500">
                              <CheckCircle className="w-4 h-4 mr-2" />
                              <span>Helpful answer? Contact us if you need more assistance</span>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        {expandedQuestions.includes(faq.id) ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
              <HelpCircle className="w-16 h-16 text-gray-300 mx-auto mb-6" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No results found
              </h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                {searchQuery
                  ? `We couldn't find any answers matching "${searchQuery}". Try different keywords or browse by category.`
                  : 'No questions available in this category.'}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => setActiveCategory('all')}
                  className="px-6 py-2 rounded-lg font-medium"
                  style={{ 
                    backgroundColor: brandColorLight,
                    color: brandColor
                  }}
                >
                  View All Questions
                </button>
                <button
                  onClick={() => navigate('/contact')}
                  className="px-6 py-2 border-2 rounded-lg font-medium"
                  style={{ 
                    borderColor: brandColor,
                    color: brandColor
                  }}
                >
                  Ask a Question
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Contact Options */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4"
                 style={{ backgroundColor: brandColorLight }}>
              <Mail className="w-6 h-6" style={{ color: brandColor }} />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Email Support</h3>
            <p className="text-gray-600 mb-4">Get detailed help from our support team</p>
            <button
              onClick={() => navigate('/contact')}
              className="text-sm font-medium"
              style={{ color: brandColor }}
            >
              Send Email →
            </button>
          </div>
          
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4"
                 style={{ backgroundColor: brandColorLight }}>
              <MessageSquare className="w-6 h-6" style={{ color: brandColor }} />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Live Chat</h3>
            <p className="text-gray-600 mb-4">Real-time help during business hours</p>
            <button
              onClick={() => navigate('/support/new-ticket')}
              className="text-sm font-medium"
              style={{ color: brandColor }}
            >
              Start Chat →
            </button>
          </div>
          
          
        </div>

        {/* Still Need Help? */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-8 text-white text-center">
          <div className="max-w-2xl mx-auto">
            <HelpCircle className="w-16 h-16 mx-auto mb-6" />
            <h2 className="text-2xl font-bold mb-4">Still Need Help?</h2>
            <p className="mb-8 text-lg opacity-95">
              Can't find what you're looking for? Our support team is ready to help you with any questions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/contact')}
                className="px-8 py-3 bg-white text-orange-600 font-semibold rounded-lg hover:bg-orange-50 transition-colors"
              >
                Contact Support
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
      <MobileBottomNav />
    </div>
  );
};

export default FAQPage;